import React from "react";

import { Link } from "react-router-dom";
import { DateTime } from "luxon";
import { useSWRConfig } from "swr";
import {
  format,
  formatDistanceStrict,
  intervalToDuration,
  isBefore,
  addDays
} from "date-fns";
import { adjustDate } from "../../../../helpers/date";

const DaysMap = new Map([
  ["MON", "Monday"],
  ["TUE", "Tuesday"],
  ["WED", "Wednesday"],
  ["THU", "Thursday"],
  ["FRI", "Friday"],
  ["SAT", "Saturday"],
  ["SUN", "Sunday"]
]);

const daysOrder = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const CardContent = ({ children }) => (
  <div className="tw-flex tw-w-full tw-justify-between tw-bg-white tw-pt-4 md:tw-px-8 tw-p-2 sm:tw-px-8 tw-text-gray-500 tw-pt-2 tw-font-normal tw-text-sm ">
    {children}
  </div>
);

const BoardItem = ({ vacancy }) => {
  const { mutate, fetcher } = useSWRConfig();
  const prefetchVacancy = vacancyID => {
    mutate(`/vacancies/${vacancyID}`, fetcher(`/vacancies/${vacancyID}`));
  };
  const getFullDayName = name => DaysMap.get(name);

  const determineDurationInDays = (startDate, endDate) => {
    if (startDate === endDate) {
      return "1 day";
    }
    const newEndDate = new Date(endDate);
    const endDatePlusDay = newEndDate.setDate(newEndDate.getDate() + 1);
    return `${formatDistanceStrict(
      new Date(startDate),
      new Date(endDatePlusDay)
    )}`;
  };

  const determineDurationInHours = (startHours, endHours) => {
    let time;

    if (isBefore(new Date(startHours), new Date(endHours))) {
      time = intervalToDuration({
        start: new Date(startHours),
        end: new Date(endHours)
      });
    } else {
      const endDatePlusDay = addDays(new Date(endHours), 1);

      time = intervalToDuration({
        start: new Date(startHours),
        end: endDatePlusDay
      });
    }

    return time.minutes === 0
      ? `${time.hours}h`
      : `${time.hours}h ${time.minutes}m`;
  };

  const adjustStartTime = adjustDate(vacancy.workHoursStart);
  const adjustEndTime = adjustDate(vacancy.workHoursEnd);

  return (
    <div className="tw-mb-4 tw-rounded-lg tw-max-w-full tw-bg-white tw-flex tw-flex-col tw-rounded tw-overflow-hidden tw-shadow-lg">
      <div className=" tw-bg-gray-100 tw-p-1">
        <h1 className="tw-p-2 tw-capitalize tw-ml-2 tw-text-lg tw-font-bold tw-font-normal">
          {vacancy.title}
        </h1>
      </div>
      <CardContent>
        <div className="tw-flex tw-flex-col tw-w-1/2 lg:tw-w-4/12">
          Location:
          <p className="tw-font-bold tw-text-xs">
            {vacancy.location &&
            !vacancy.address &&
            !vacancy.town &&
            !vacancy.county &&
            !vacancy.postcode
              ? vacancy.location
              : `${vacancy.address}, ${vacancy.town}, ${vacancy.county}, ${vacancy.postcode}`}
          </p>
        </div>
        <div className="lg:tw-visible tw-invisible tw-flex tw-flex-col tw-w-0 lg:tw-w-4/12">
          Created By:
          <p className="tw-font-bold tw-text-xs">{vacancy.creatorEmail}</p>
        </div>
        <div className="tw-font-normal tw-w-1/2 lg:tw-w-1/12 tw-flex tw-text-sm tw-justify-end">
          <div className="tw-flex-col tw-flex tw-text-right">
            Status:
            <p className="tw-font-bold tw-text-md">{vacancy.state}</p>
          </div>
        </div>
      </CardContent>
      <div className="tw-visible lg:tw-invisible lg:tw-h-0 tw-flex tw-flex-col tw-w-full md:tw-px-8 tw-p-2 sm:tw-px-8 tw-text-gray-500 tw-font-normal tw-text-sm">
        <div>
          Created By:
          <p className="tw-font-bold">{vacancy.creatorEmail}</p>
        </div>
      </div>
      <CardContent>
        <div className="tw-w-1/2 md:tw-w-auto">
          <div>
            Working Hours:
            <p className="tw-font-bold tw-text-xs">
              {determineDurationInHours(
                vacancy.workHoursStart,
                vacancy.workHoursEnd
              )}
            </p>
          </div>
          <div>
            Duration:
            <p className="tw-font-bold tw-text-xs">
              {vacancy.endDate &&
                determineDurationInDays(vacancy.startDate, vacancy.endDate)}
            </p>
          </div>
        </div>
        <div className="tw-w-1/2 md:tw-w-auto">
          <div>
            Start Time:
            <p className="tw-font-bold tw-text-xs">
              {DateTime.fromISO(adjustStartTime).toLocaleString(
                DateTime.TIME_SIMPLE
              )}
            </p>
          </div>
          <div>
            End Time:
            <p className="tw-font-bold tw-text-xs">
              {DateTime.fromISO(adjustEndTime).toLocaleString(
                DateTime.TIME_SIMPLE
              )}
            </p>
          </div>
        </div>
        <div className="tw-w-1/2 md:tw-w-auto">
          <div>
            Start Date:
            <p className="tw-font-bold tw-text-xs">
              {format(new Date(vacancy.startDate), "dd/MM/yyyy")}
            </p>
          </div>
          <div>
            End Date:
            <p className="tw-font-bold tw-text-xs">
              {vacancy.endDate
                ? format(new Date(vacancy.endDate), "dd/MM/yyyy")
                : ""}
            </p>
          </div>
        </div>
        <div className="tw-w-1/2 md:tw-w-auto">
          <div>
            Created:
            <p className="tw-font-bold tw-text-xs">
              {format(new Date(vacancy.createdAt), "dd/MM/yyyy")}
            </p>
          </div>
          <div>
            Modified:
            <p className="tw-font-bold tw-text-xs">
              {format(new Date(vacancy.updatedAt), "dd/MM/yyyy")}
            </p>
          </div>
        </div>
      </CardContent>
      <div className="tw-flex tw-w-full md:tw-flex-row tw-justify-between tw-flex-col tw-bg-white tw-pt-4 md:tw-px-8 tw-p-2 sm:tw-px-8 tw-text-gray-500 tw-pt-2 tw-font-normal tw-text-sm ">
        <div>
          Working Days:
          <div className="tw-pt-2 tw-flex tw-flex-wrap ">
            {vacancy.workDays.length !== 0 ? (
              vacancy.workDays
                .sort((a, b) => {
                  if (daysOrder.indexOf(a) > daysOrder.indexOf(b)) {
                    return 1;
                  }
                  return -1;
                })
                .map((day, i) => (
                  <p key={i} className="tw-font-bold tw-pr-2 tw-text-xs">
                    {getFullDayName(day)}
                  </p>
                ))
            ) : (
              <p className="tw-font-bold tw-pr-2 tw-text-xs">
                Working days are not specified
              </p>
            )}
          </div>
        </div>
        <div>
          <div className="tw-mt-4 md:tw-m-0">
            Required Workers:{" "}
            <span className="tw-font-bold">{vacancy.workersRequired}</span>
          </div>
          <div className="tw-mt-4 md:tw-m-0">
            Pay Rate:{" "}
            <span className="tw-font-bold">
              £{vacancy.payRate ? vacancy.payRate.toFixed(2) : ""}{" "}
              {vacancy.payRateFreq ? vacancy.payRateFreq : ""}
            </span>
          </div>
          {!!vacancy.chargeRate && (
            <div className="tw-mt-4 md:tw-m-0">
              Charge Rate:{" "}
              <span className="tw-font-bold">
                £{vacancy.chargeRate.toFixed(2)}{" "}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="tw-mt-4 tw-items-center tw-bg-gray-100 tw-flex tw-p-2 ">
        {!["CLOSED", "APPROVED", "CANCELLED", "COMPLETED"].includes(
          vacancy.state
        ) && (
          <div className="tw-text-sm tw-mx-2 tw-flex tw-justify-start">
            <Link
              to={`vacancies/edit/${vacancy.vacancyID}`}
              className="tw-bg-gray-300  tw-text-gray-800 tw-py-2 tw-px-4 tw-rounded"
            >
              Edit
            </Link>
          </div>
        )}
        <div className="tw-text-sm tw-mx-2 tw-w-full tw-flex tw-justify-end">
          <Link
            to={`vacancies/${vacancy.vacancyID}`}
            className="tw-bg-gray-300  tw-text-gray-800 tw-py-2 tw-px-4 tw-rounded"
            onMouseEnter={() => prefetchVacancy(vacancy.vacancyID)}
          >
            Vacancy details
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BoardItem;
